import { useState, useMemo, useRef, useEffect } from 'react'
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { ChevronDown, Check, X, Search } from 'lucide-react'

/* ==========================================================================
   Combobox Component
   Based on design.md specifications
   Searchable dropdown with grouped options support
   Task F30: Updated to use pharmacy colors for focus states
   ========================================================================== */

export interface ComboboxOption {
  value: string
  label: string
  /** Optional secondary text (e.g., product description) */
  description?: string
  disabled?: boolean
}

export interface ComboboxOptionGroup {
  label: string
  options: ComboboxOption[]
}

export interface ComboboxProps {
  /** Options to display - can be flat or grouped */
  options: ComboboxOption[] | ComboboxOptionGroup[]
  /** Currently selected value */
  value: string | null
  /** Callback when selection changes */
  onChange: (value: string | null) => void
  /** Placeholder text when no selection */
  placeholder?: string
  /** Disable the combobox */
  disabled?: boolean
  /** Error state styling */
  error?: boolean
  /** Show clear button when value selected */
  clearable?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Check if options are grouped
 */
function isGroupedOptions(
  options: ComboboxOption[] | ComboboxOptionGroup[]
): options is ComboboxOptionGroup[] {
  return options.length > 0 && 'options' in options[0]
}

/**
 * Flatten grouped options for searching
 */
function flattenOptions(
  options: ComboboxOption[] | ComboboxOptionGroup[]
): ComboboxOption[] {
  if (isGroupedOptions(options)) {
    return options.flatMap((group) => group.options)
  }
  return options
}

/**
 * Searchable combobox component with grouped options support.
 * Uses Headless UI Combobox for full accessibility.
 *
 * @example
 * ```tsx
 * // Flat options
 * <Combobox
 *   options={[
 *     { value: '1', label: 'Product A' },
 *     { value: '2', label: 'Product B' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 *   placeholder="Search products..."
 * />
 *
 * // Grouped options
 * <Combobox
 *   options={[
 *     {
 *       label: 'Hormone Therapy',
 *       options: [
 *         { value: '1', label: 'Testosterone', description: '200mg/mL' },
 *       ],
 *     },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 * ```
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  error = false,
  clearable = true,
  className = '',
}: ComboboxProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Debounce search query to avoid filtering/rendering on every keystroke
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const allOptions = useMemo(() => flattenOptions(options), [options])
  const selectedOption = allOptions.find((opt) => opt.value === value)

  const MAX_RENDERED = 50

  // Filter options based on debounced search query
  const filteredOptions = useMemo(() => {
    let filtered: ComboboxOption[] | ComboboxOptionGroup[]

    if (!debouncedQuery) {
      filtered = options
    } else {
      const lowerQuery = debouncedQuery.toLowerCase()

      if (isGroupedOptions(options)) {
        filtered = options
          .map((group) => ({
            ...group,
            options: group.options.filter(
              (opt) =>
                opt.label.toLowerCase().includes(lowerQuery) ||
                opt.value.toLowerCase().includes(lowerQuery) ||
                opt.description?.toLowerCase().includes(lowerQuery)
            ),
          }))
          .filter((group) => group.options.length > 0)
      } else {
        filtered = options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(lowerQuery) ||
            opt.value.toLowerCase().includes(lowerQuery) ||
            opt.description?.toLowerCase().includes(lowerQuery)
        )
      }
    }

    // Cap rendered options to avoid DOM overload with large datasets
    const totalCount = isGroupedOptions(filtered)
      ? filtered.reduce((sum, g) => sum + g.options.length, 0)
      : filtered.length

    if (totalCount <= MAX_RENDERED) return { options: filtered, truncated: false }

    if (isGroupedOptions(filtered)) {
      let remaining = MAX_RENDERED
      const capped = filtered
        .map((group) => {
          if (remaining <= 0) return { ...group, options: [] }
          const sliced = group.options.slice(0, remaining)
          remaining -= sliced.length
          return { ...group, options: sliced }
        })
        .filter((group) => group.options.length > 0)
      return { options: capped, truncated: true }
    }

    return { options: (filtered as ComboboxOption[]).slice(0, MAX_RENDERED), truncated: true }
  }, [options, debouncedQuery])

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setQuery('')
    setDebouncedQuery('')
  }

  const inputBaseStyles = [
    // Layout
    'w-full',
    'pl-10 pr-10 py-3',
    // Typography
    'text-base',
    'font-sans',
    'text-[var(--color-charcoal)]',
    'placeholder:text-[var(--color-gray-500)]',
    // Background and border
    'bg-white',
    'border',
    'rounded-md',
    // Transitions
    'transition-[border-color,box-shadow] duration-150 ease-in-out',
    // Focus state
    'focus:outline-none',
  ]

  const inputStateStyles = error
    ? [
        // Error state (not customizable per design.md)
        'border-[var(--color-error)]',
        'focus:border-[var(--color-error)]',
        'focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]',
      ]
    : [
        // Default state - uses pharmacy colors for white-labeling (Task F30)
        'border-[var(--color-gray-300)]',
        'focus:border-[var(--pharmacy-primary)]',
        'focus:shadow-[0_0_0_3px_var(--pharmacy-focus-ring)]',
      ]

  const inputDisabledStyles = disabled
    ? [
        'bg-[var(--color-gray-100)]',
        'text-[var(--color-gray-500)]',
        'cursor-not-allowed',
      ]
    : []

  const inputClasses = [
    ...inputBaseStyles,
    ...inputStateStyles,
    ...inputDisabledStyles,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderOption = (option: ComboboxOption) => (
    <ComboboxOption
      key={option.value}
      value={option.value}
      disabled={option.disabled}
      className="group relative cursor-pointer select-none py-2.5 pl-10 pr-4 data-[focus]:bg-[var(--color-soft-lavender)] data-[selected]:bg-[var(--color-soft-lavender)] data-[disabled]:cursor-not-allowed data-[disabled]:text-[var(--color-gray-400)]"
    >
      {({ selected }) => (
        <>
          <div className="flex flex-col">
            <span
              className={`block truncate text-[var(--color-charcoal)] ${selected ? 'font-medium' : 'font-normal'}`}
            >
              {option.label}
            </span>
            {option.description && (
              <span className="block truncate text-sm text-[var(--color-gray-600)]">
                {option.description}
              </span>
            )}
          </div>
          {selected && (
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--pharmacy-primary)]">
              <Check className="h-5 w-5" aria-hidden="true" />
            </span>
          )}
        </>
      )}
    </ComboboxOption>
  )

  const hasResults = isGroupedOptions(filteredOptions.options)
    ? filteredOptions.options.some((g) => g.options.length > 0)
    : filteredOptions.options.length > 0

  return (
    <HeadlessCombobox
      value={value}
      onChange={(newValue) => {
        onChange(newValue)
        setQuery('')
        setDebouncedQuery('')
      }}
      disabled={disabled}
    >
      {({ open }) => (
        <div className="relative">
          {/* Search icon */}
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search
              className="h-5 w-5 text-[var(--color-gray-500)]"
              aria-hidden="true"
            />
          </span>

          <ComboboxInput
            className={inputClasses}
            displayValue={() => (selectedOption ? selectedOption.label : '')}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-invalid={error || undefined}
          />

          {/* Right side buttons */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
            {/* Clear button */}
            {clearable && value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 rounded hover:bg-[var(--color-gray-100)] transition-colors"
                aria-label="Clear selection"
              >
                <X
                  className="h-4 w-4 text-[var(--color-gray-500)]"
                  aria-hidden="true"
                />
              </button>
            )}

            {/* Chevron button */}
            <ComboboxButton className="p-1">
              <ChevronDown
                className={`h-5 w-5 text-[var(--color-gray-500)] transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>

          <ComboboxOptions
            transition
            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:duration-150 data-[leave]:duration-100"
          >
            {!hasResults && debouncedQuery !== '' ? (
              <div className="relative cursor-default select-none py-3 px-4 text-[var(--color-gray-500)]">
                No results found
              </div>
            ) : isGroupedOptions(filteredOptions.options) ? (
              <>
                {filteredOptions.options.map((group) => (
                  <div key={group.label}>
                    <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-gray-500)] bg-[var(--color-gray-50)]">
                      {group.label}
                    </div>
                    {group.options.map(renderOption)}
                  </div>
                ))}
                {filteredOptions.truncated && (
                  <div className="px-4 py-2.5 text-sm text-[var(--color-gray-500)] bg-[var(--color-gray-50)] text-center">
                    Type to narrow results...
                  </div>
                )}
              </>
            ) : (
              <>
                {(filteredOptions.options as ComboboxOption[]).map(renderOption)}
                {filteredOptions.truncated && (
                  <div className="px-4 py-2.5 text-sm text-[var(--color-gray-500)] bg-[var(--color-gray-50)] text-center">
                    Type to narrow results...
                  </div>
                )}
              </>
            )}
          </ComboboxOptions>
        </div>
      )}
    </HeadlessCombobox>
  )
}

export default Combobox
